import React, { createContext, useState, useContext, useRef, useEffect } from 'react';
import { getRandomSongs } from './utils/songUtils';
import audioService from './utils/audioService';
import { useLocation } from 'react-router-dom';

// Add polyfill for requestIdleCallback
const requestIdleCallbackPolyfill = (callback) => {
  const start = Date.now();
  return setTimeout(() => {
    callback({
      didTimeout: false,
      timeRemaining: () => Math.max(0, 50 - (Date.now() - start))
    });
  }, 1);
};

// Use the native version if available, otherwise use the polyfill
const requestIdle = window.requestIdleCallback || requestIdleCallbackPolyfill;

const SongContext = createContext();

export const useSongContext = () => useContext(SongContext);

export const SongProvider = ({ children }) => {
    const [currentSongId, setCurrentSongId] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentProgress, setCurrentProgress] = useState(0);
    const [songsLoaded, setSongsLoaded] = useState({});
    const audioRef = useRef(null);
    const isTransitioning = useRef(false);
    const location = useLocation();
    const lastPathRef = useRef(location.pathname);

    // Ref to hold the latest currentSongId for use in callbacks
    const currentSongIdRef = useRef(currentSongId);
    // Ref to track the intended song to play (for robust callback handling)
    const intendedSongIdRef = useRef(null);

    // This effect updates the ref whenever currentSongId changes
    useEffect(() => {
        currentSongIdRef.current = currentSongId;
        console.log('[SongContext] currentSongId changed:', currentSongId);
    }, [currentSongId]);

    useEffect(() => {
        console.log('[SongContext] isPlaying changed:', isPlaying);
    }, [isPlaying]);

    useEffect(() => {
        console.log('[SongContext] currentProgress changed:', currentProgress);
    }, [currentProgress]);

    // Effect to handle route changes
    useEffect(() => {
        // Only stop playback if we've actually changed pages
        if (location.pathname !== lastPathRef.current) {
            // Stop playback
            if (isPlaying) {
                audioService.pause();
                setIsPlaying(false);
                setCurrentSongId(null);
                setCurrentProgress(0);
            }
            // Update the last path
            lastPathRef.current = location.pathname;
        }
    }, [location.pathname, isPlaying]);

    // Set up audio service callbacks when component mounts
    useEffect(() => {
        audioService.registerCallbacks({
            onPlay: (songId) => {
                console.log('[SongContext] onPlay callback:', { songId, intended: intendedSongIdRef.current });
                if (songId === intendedSongIdRef.current) {
                    console.log('[SongContext] onPlay: updating state for songId', songId);
                    setCurrentSongId(songId);
                    setIsPlaying(true);
                } else {
                    console.log('[SongContext] onPlay: NOT updating state, songId does not match intended');
                }
            },
            onPause: (songId) => {
                console.log('[SongContext] onPause callback:', { songId, intended: intendedSongIdRef.current });
                if (songId === intendedSongIdRef.current) {
                    console.log('[SongContext] onPause: updating isPlaying to false');
                    setIsPlaying(false);
                } else {
                    console.log('[SongContext] onPause: NOT updating, songId does not match intended');
                }
            },
            onEnd: () => {
                console.log('[SongContext] onEnd callback:', { currentSongId: currentSongIdRef.current, intended: intendedSongIdRef.current });
                if (currentSongIdRef.current === intendedSongIdRef.current) {
                    console.log('[SongContext] onEnd: updating isPlaying to false and progress to 0');
                    setIsPlaying(false);
                    setCurrentProgress(0);
                } else {
                    console.log('[SongContext] onEnd: NOT updating, currentSongId does not match intended');
                }
            },
            onLoad: (songId) => {
                console.log('[SongContext] onLoad callback:', { songId });
                setSongsLoaded(prev => ({
                    ...prev,
                    [songId]: true
                }));
            },
            onSeek: (songId, progress) => {
                console.log('[SongContext] onSeek callback:', { songId, progress, currentSongId: currentSongIdRef.current });
                if (songId === currentSongIdRef.current) {
                    setCurrentProgress(progress);
                }
            },
            onProgress: (songId, progress) => {
                // This can be noisy, so only log if songId matches
                if (songId === currentSongIdRef.current) {
                    // Uncomment for more verbose logging:
                    // console.log('[SongContext] onProgress callback:', { songId, progress });
                    setCurrentProgress(progress);
                }
            }
        });

        // Clean up on unmount
        return () => {
            audioService.reset();
        };
    }, []); // Changed dependency array

    /**
     * Load audio for a specific song
     * @param {string} songId - Song ID
     * @param {string} filePath - Path to the audio file
     * @returns {Promise<{success: boolean, error?: Error}>}
     */
    const loadAudioBuffer = async (songId, filePath) => {
        try {
            // Check if already loaded
            if (songsLoaded[songId]) {
                return { success: true };
            }
            
            const success = await audioService.loadSong(songId, filePath);
            if (success) {
                requestIdle(() => {
                    setSongsLoaded(prev => ({
                        ...prev,
                        [songId]: true
                    }));
                });
            }
            
            return { success };
        } catch (error) {
            console.error('Error loading audio:', error);
            return { success: false, error };
        }
    };

    /**
     * Play a specific song
     * @param {string} songId - Song ID
     * @param {string} filePath - Path to the audio file
     * @param {number} [startPosition=0] - Starting position in percentage (0-100)
     * @returns {Promise<boolean>}
     */
    const playSong = async (songId, filePath, startPosition = 0) => {
        console.log('[SongContext] playSong called:', { songId, filePath, startPosition });
        // Log when intendedSongIdRef is set
        console.log('[SongContext] Setting intendedSongIdRef.current =', songId);
        try {
            if (isTransitioning.current) return false;
            isTransitioning.current = true;

            // Always stop current playback first
            if (currentSongId) {
                audioService.pause();
                setIsPlaying(false);
            }

            // Track the intended song to play
            intendedSongIdRef.current = songId;

            setCurrentSongId(songId);
            setIsPlaying(true);
            if (startPosition === 0) {
                setCurrentProgress(0);
            }

            // Small delay to ensure clean transition
            await new Promise(resolve => setTimeout(resolve, 10));

            const success = await audioService.playSong(songId, filePath, startPosition);
            
            return success;
        } catch (error) {
            console.error('Error playing song:', error);
            setIsPlaying(false);
            return false;
        } finally {
            isTransitioning.current = false;
        }
    };

    /**
     * Pause current playback
     */
    const pausePlayback = () => {
        if (isTransitioning.current) return;
        audioService.pause();
        setIsPlaying(false);
    };

    /**
     * Resume current playback
     * @returns {Promise<boolean>}
     */
    const resumePlayback = async () => {
        if (isTransitioning.current) return false;
        
        const success = audioService.resume();
        if (success) {
            setIsPlaying(true);
        }
        return success;
    };

    /**
     * Seek to position in current song
     * @param {number} position - Position in percentage (0-100)
     */
    const seekTo = (position) => {
        if (isTransitioning.current) return;
        console.log('[SongContext] seekTo called with position:', position, 'currentSongId:', currentSongId);
        audioService.seekTo(position);
        setCurrentProgress(position);
    };

    const fetchRandomSongs = async (count = 1) => {
        return await getRandomSongs(count);
    };

    return (
        <SongContext.Provider value={{ 
            currentSongId, 
            setCurrentSongId,
            isPlaying,
            currentProgress,
            audioRef,
            loadAudioBuffer,
            playSong,
            pausePlayback,
            resumePlayback,
            seekTo,
            fetchRandomSongs,
            songsLoaded
        }}>
            {children}
        </SongContext.Provider>
    );
};